import React, { useEffect, useRef } from "react";
import { GENRES } from "../../../api/presets";

// design doc "filter-sort-design.md" section 4: single-select genre chips.
// This is a FILTER (accent border when selected), never confused with the
// SORT segments (white when selected) in SortSegments.jsx.
const CHIP_BASE =
  "inline-flex h-9 shrink-0 items-center rounded-full border px-3 font-mono text-xs uppercase tracking-wider transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]";
const CHIP_IDLE = "border-white/10 text-[#A1A1AA] hover:border-white/25 hover:text-white";
const CHIP_SELECTED = "border-[#FF5500] bg-[#FF5500]/10 text-[#FF5500]";

const OPTIONS = ["", ...GENRES]; // "" is the "All" chip

function Chip({ value, label, selected, onSelect, chipRef }) {
  return (
    <button
      ref={chipRef}
      type="button"
      aria-pressed={selected}
      // Clicking an already-selected chip does nothing -- only the `All`
      // chip can get you back to "no genre" (design section 4).
      onClick={() => !selected && onSelect(value)}
      className={`${CHIP_BASE} ${selected ? CHIP_SELECTED : CHIP_IDLE}`}
    >
      {label}
    </button>
  );
}

export default function GenreChips({ genre, onChange }) {
  const selectedRef = useRef(null);

  useEffect(() => {
    // The selected chip is scrolled into view on mount (design section 3.2)
    // -- matters on the mobile horizontal strip, where a genre set from a
    // pasted link could otherwise start off-screen.
    selectedRef.current?.scrollIntoView({ block: "nearest", inline: "center" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex items-center gap-3">
      <span className="w-14 shrink-0 font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">
        Genre
      </span>
      <div className="relative flex-1 min-w-0">
        <div
          role="group"
          aria-label="Genre"
          className="flex gap-2 overflow-x-auto py-1 -mr-6 pr-6 sm:mr-0 sm:pr-0 sm:flex-wrap sm:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {OPTIONS.map((value) => {
            const selected = genre === value;
            return (
              <Chip
                key={value || "all"}
                value={value}
                label={value || "All"}
                selected={selected}
                onSelect={onChange}
                chipRef={selected ? selectedRef : null}
              />
            );
          })}
        </div>
        {/* Scroll cue on mobile only; at sm+ the strip wraps instead of scrolling. */}
        <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-[#050505] sm:hidden" />
      </div>
    </div>
  );
}
